import { Translation } from "@cryptors/api-client";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import { List, useDataGrid } from "@refinedev/mui";
import React, { useMemo } from "react";

import {
  useDeleteTranslation,
  useUpdateTranslation,
} from "@/api/translation.api";
import { SearchField } from "@/components/forms/SearchField";

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const orValue: CrudFilter[] = [
    { field: "value", operator: "containss", value: trimmed },
    { field: "languageCode", operator: "containss", value: trimmed },
  ];
  if (/^\d+$/.test(trimmed)) {
    orValue.push({ field: "id", operator: "eq", value: Number(trimmed) });
  }
  return [{ operator: "or", value: orValue }];
};

export const TranslationList: React.FC<IResourceComponentsProps> = () => {
  const { dataGridProps, setFilters } = useDataGrid<Translation>({
    resource: "translation",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "id", order: "desc" }] },
  });

  const { mutateAsync: updateTranslation } = useUpdateTranslation();
  const { mutateAsync: deleteTranslation } = useDeleteTranslation();

  const columns = useMemo<GridColDef<Translation>[]>(
    () => [
      { field: "id", headerName: "Id", type: "number", minWidth: 50 },
      { field: "languageCode", headerName: "Langue", minWidth: 50 },
      {
        field: "value",
        headerName: "Valeur",
        minWidth: 200,
        flex: 1,
        editable: true,
      },
      {
        field: "action",
        headerName: "Actions",
        minWidth: 120,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (record) => {
          return (
            <>
              <Tooltip title="Sauvegarder">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      updateTranslation({
                        id: record.id as number,
                        value: record.row.value,
                      });
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Supprimer">
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Supprimer cette traduction ? Action définitive.",
                        )
                      ) {
                        deleteTranslation(record.id as number);
                      }
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          );
        },
      },
    ],
    [updateTranslation, deleteTranslation],
  );

  return (
    <List title="Traduction">
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (valeur, langue, id)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
